import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { Field, FormSpy } from "react-final-form";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import {
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
} from "@material-ui/core";

import ContentStudioWorkoutsStore from "stores/ContentStudioWorkoutsStore";
import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import TagDictionary from "stores/dictionary/TagDictionary";
import CategoryDictionary from "stores/dictionary/CategoryDictionary";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import TagsControl from "components/TagsControl";
import SimpleField from "components/SimpleField";

import EquipmentControl from "./EquipmentControl";
import MovementsControl from "./MovementsControl";

import MovementCard from "./MovementsControl/MovementCard";
import EquipmentCard from "./EquipmentControl/EquipmentCard";

import { EquipmentsContainer } from "./EquipmentControl/styles";
import { MovementCards } from "./MovementsControl/styles";

import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  compose,
  fileSizeValidator,
  imageIsCorruptedValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";

import { PUBLISH_STATUSES, WORKOUT_STRUCTURE_TYPES } from "constants/models";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";

import ROUTES from "navigation/routes";

import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  GroupRowLabelContainer,
  Row,
  Divider,
  UploadPreviewFieldControl,
  ImagePreviewContainer,
  MovementsCardWrapper,
  PublishStatusWrapper,
} from "./styles";
import TargetZoneDictionary from "stores/dictionary/TargetZoneDictionary";
import { getMessage } from "utils/messages";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<
  { workoutId: string; id?: string },
  {},
  LocationState
> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class WorkoutDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewWorkout() {
    const { workoutId } = this.props.match.params;

    return workoutId === undefined;
  }

  get workoutId() {
    const { workoutId } = this.props.match.params;

    const parsedWorkoutId = Number.parseInt(workoutId);
    return typeof parsedWorkoutId === "number" ? parsedWorkoutId : undefined;
  }

  get categoryId() {
    const { id } = this.props.match.params;

    const categoryId = id ? Number.parseInt(id) : undefined;
    return typeof categoryId === "number" ? categoryId : undefined;
  }

  get backClickText() {
    return this.categoryId ? "Back to Category Details" : " Back to Workouts";
  }

  get backClickPath() {
    return this.categoryId
      ? generatePath(ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS, {
          id: this.categoryId,
        })
      : ROUTES.CONTENT_STUDIO_WORKOUTS;
  }

  @computed get categoryOptions() {
    const weekCategoryOptions =
      this.initialValues.category
        ?.filter(item => item.structureType === WORKOUT_STRUCTURE_TYPES.WEEK)
        .map(item => ({
          value: item,
          label: item.name,
          isFixed: true,
          isDisabled: true,
        })) ?? [];

    return [...CategoryDictionary.options, ...weekCategoryOptions];
  }

  @computed get getTargetZoneOptions() {
    console.log(TargetZoneDictionary.options);
    return [...TargetZoneDictionary.options];
  }

  @computed get displayValues(): any {
    const formData = ContentStudioWorkoutsStore.details?.formData;
    const details = ContentStudioWorkoutsStore.details;

    return {
      ...formData,
      category: formData?.category?.map(({ name }) => name).join(", "),
      tags: formData?.tags?.map(
        ({ id, title }: { id: number; title: string }) => ({ id, name: title }),
      ),
      dontAwardStar: !details?.awardStar || false,
    };
  }

  @computed get initialValues() {
    if (this.isNewWorkout) {
      return {
        dontAwardStar: false,
      };
    } else {
      return {
        ...ContentStudioWorkoutsStore.details?.formData,
        dontAwardStar: !ContentStudioWorkoutsStore.details?.formData.awardStar,
      };
    }
  }

  @computed get workoutPublishStatus() {
    return ContentStudioWorkoutsStore.details?.publishStatus;
  }

  @computed get durationText() {
    return this.displayValues?.isVideoReady
      ? this.displayValues?.durationText
      : "Video processing is still in progress. Video duration will be automatically updated later.";
  }

  async componentDidMount() {
    const { history } = this.props;

    Promise.all([
      ActiveProgramDictionary.fetch(),
      CategoryDictionary.fetch(),
      TagDictionary.fetch(),
      TargetZoneDictionary.fetch(),
    ]);

    if (this.isNewWorkout) {
      history.replace({ state: { editMode: true } });
    } else {
      await ContentStudioWorkoutsStore.fetchWorkoutDetails(this.workoutId);
      await ContentStudioWorkoutsStore.fetchTargetZoneTagging(this.workoutId);
    }
  }

  componentWillUnmount() {
    ContentStudioWorkoutsStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewWorkout) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
      workoutId: this.workoutId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
      workoutId: this.workoutId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.workoutPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.workoutPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const {
      file,
      id,
      category,
      instruction,
      title,
      videoUrl,
      tags,
      equipments,
      movements,
      targetZone,
      dontAwardStar,
    } = values;

    const data = {
      categoryIds: category
        ?.filter(
          ({ structureType }: { structureType: string }) =>
            structureType !== WORKOUT_STRUCTURE_TYPES.WEEK,
        )
        .map(({ id }: { id: number }) => id),
      instruction: instruction?.trim(),
      title: title?.trim(),
      videoUrl: videoUrl?.trim(),
      tagIds: tags?.map(({ id }: { id: number }) => id),
      equipmentIds: equipments?.map(({ id }: { id: number }) => id),
      movementIds: movements?.map(({ id }: { id: number }) => id),
      awardStar: !dontAwardStar,
    };

    try {
      if (this.isNewWorkout) {
        const payload = { ...data, imageUrl: file.dataURL };
        const id = await ContentStudioWorkoutsStore.createWorkout(payload);
        if (id) {
          const taggingTargetZone = {
            id: id,
            targetZoneIds: targetZone?.map(({ id }: { id: number }) => id),
          };

          await ContentStudioWorkoutsStore.createTaggingTargetZone(
            taggingTargetZone,
          );

          const path = generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
            workoutId: id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          await ContentStudioWorkoutsStore.fetchWorkoutDetails(id);
          await ContentStudioWorkoutsStore.fetchTargetZoneTagging(id);
        }
      } else {
        const payload = { ...data, imageUrl: file.dataURL, id };
        await ContentStudioWorkoutsStore.editWorkoutInfo(payload);
        const taggingTargetZone = {
          id: id,
          targetZoneIds: targetZone?.map(({ id }: { id: number }) => id),
        };

        await ContentStudioWorkoutsStore.createTaggingTargetZone(
          taggingTargetZone,
        );

        if (typeof file !== "string") {
          const payload = { file, id };
          await ContentStudioWorkoutsStore.editWorkoutImage(payload);
        }

        this.exitEditMode();
        await ContentStudioWorkoutsStore.fetchWorkoutDetails(id);
        await ContentStudioWorkoutsStore.fetchTargetZoneTagging(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Workout Details"
        leftControls={
          this.workoutPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.workoutPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickText}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={ContentStudioWorkoutsStore.isWorkoutLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewWorkout}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{ dirty: true }}
            onChange={this.onChangeIsPopupDirty}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column width="50%">
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="file"
                      acceptedFiles={ACCEPTED_IMAGE_TYPES}
                      validate={compose(
                        this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                        fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                        imageIsCorruptedValidator,
                      )}
                      required
                    />
                  ) : (
                    <ImagePreviewContainer
                      image={this.displayValues?.imageUrl}
                    />
                  )}
                </Row>
              </Column>

              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="videoUrl"
                        label="URL"
                        fullWidth
                      />
                    ) : (
                      <SimpleField
                        label="URL"
                        value={this.displayValues?.videoUrl}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="category"
                        options={this.categoryOptions}
                        label="Category"
                        multi
                        isClearable
                      />
                    ) : (
                      <SimpleField
                        label="Category"
                        value={this.displayValues?.category}
                      />
                    )}
                  </Column>
                  <Column>
                    {this.isEditMode ? null : (
                      <SimpleField
                        label="Duration"
                        linesCount={3}
                        value={this.durationText}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="targetZone"
                        options={this.getTargetZoneOptions}
                        label="Target Zone"
                        multi
                        isClearable
                      />
                    ) : (
                      <SimpleField
                        label="Target Zone"
                        value={this.displayValues?.targetZone}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Field name="dontAwardStar" type="checkbox">
                    {({ input }) => (
                      <FormControlLabel
                        control={<Checkbox {...input} color="primary" />}
                        label="Don't award Stars / don't show workout features"
                      />
                    )}
                  </Field>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.displayValues?.dontAwardStar}
                        color="primary"
                        disabled
                      />
                    }
                    label="Don't award Stars / don't show workout features"
                  />
                )}
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Row>
                    <TagsControl name="tags" options={TagDictionary.options} />
                  </Row>
                ) : (
                  <SimpleField label="Tags" value={this.displayValues?.tags} />
                )}
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                {this.isEditMode ? (
                  <Row>
                    <EquipmentControl name="equipments" />
                  </Row>
                ) : (
                  <>
                    <GroupRowLabelContainer>
                      <FormLabel>Equipment</FormLabel>
                    </GroupRowLabelContainer>
                    <EquipmentsContainer>
                      {this.displayValues?.equipments?.map((item: any) => (
                        <EquipmentCard
                          key={item.id}
                          title={item.title}
                          icon={item.imageUrl}
                        />
                      ))}
                    </EquipmentsContainer>
                  </>
                )}
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      name="instruction"
                      variant="outlined"
                      margin="none"
                      label="Instructions"
                      rows={5}
                      rowsMax={10}
                      disabled={!this.isEditMode}
                      fullWidth
                      multiline
                      required
                    />
                  ) : (
                    <SimpleField
                      label="Instructions"
                      value={this.displayValues?.instruction}
                      isRichText
                      linesCount={3}
                    />
                  )}
                </Row>

                {this.isEditMode ? (
                  <Row>
                    <MovementsControl name="movements" />
                  </Row>
                ) : (
                  <>
                    <GroupRowLabelContainer>
                      <FormLabel>Movements</FormLabel>
                    </GroupRowLabelContainer>
                    <MovementsCardWrapper>
                      <MovementCards>
                        {this.displayValues?.movements?.map((item: any) => (
                          <MovementCard
                            key={item.id}
                            title={item.title}
                            duration={item.durationText}
                            image={item.imageUrl}
                          />
                        ))}
                      </MovementCards>
                    </MovementsCardWrapper>
                  </>
                )}
              </Column>
            </GroupRow>
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ContentStudioWorkoutsStore.isWorkoutManaging}
      fullWidth
    >
      {this.isNewWorkout ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(WorkoutDetails));
